import {
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
} from 'global/constants';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ComponentEventData,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import ComponentsViewActivityPageElements from '../components-view-activity-page-elements';
import {
  ComponentDiscussion,
  ComponentDraw,
  ComponentFillInBlanks,
  ComponentInteractiveResponseCategorise,
  ComponentInteractiveResponseSequence,
  ComponentMultipleChoice,
  ComponentResponse,
  ComponentTable,
} from '../component.constants';
import ResponseViewSample from '../response/sample/response-view-sample';
import DrawViewSample from '../draw/sample/draw-view-sample';
import TableViewSample from '../table/sample/table-view-sample';
import InteractiveResponseCategoriseViewSample from '../interactive-response/categorise/sample/interactive-categorise-view-sample';
import InteractiveResponseViewSample from '../interactive-response/sequence/sample/interactive-response-view-sample';
import MultipleChoiceViewSample from '../multiple-choice/sample/multiple-choice-view-sample';
import DiscussionView from '../discussion/discussion-view';
import FillInBlanksSample from '../fill-in-blanks/sample/fill-in-blanks-sample';
import { EVENT_ACTION_EDIT } from 'approot/shared/data-tracker/data-events.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import ActivitySampleBanner from 'approot/activity-builder/components/sample/activity-sample-banner';

type Props = {
  component: InteractiveLessonSectionComponentFragment;
  eventTrackingData?: ComponentEventData;
  contentId: number;
  openModal: (component: InteractiveLessonSectionComponentFragment) => void;
};

const ActivitySampleComponent = ({
  component,
  eventTrackingData,
  contentId,
  openModal,
}: Props) => {
  const { isPublic, contentType } = useGetInteractiveLessonParams();

  const banner = (
    <ActivitySampleBanner contentType={contentType} isPublic={isPublic} />
  );

  return (
    <>
      <ComponentsViewActivityPageElements
        component={component}
        eventTrackingData={eventTrackingData}
        contentId={contentId}
        openModal={openModal}
      />

      {/* Response components */}
      {component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
        <ResponseViewSample
          {...(component as ComponentResponse)}
          banner={banner}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE && (
        <TableViewSample {...(component as ComponentTable)} banner={banner} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW && (
        <DrawViewSample
          {...(component as ComponentDraw)}
          banner={banner}
          onOpenDrawCanvas={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              expandMode: EVENT_ACTION_EDIT,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE && (
        <MultipleChoiceViewSample
          {...(component as ComponentMultipleChoice)}
          banner={banner}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE && (
        <InteractiveResponseViewSample
          {...(component as ComponentInteractiveResponseSequence)}
          banner={banner}
        />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE && (
        <InteractiveResponseCategoriseViewSample
          {...(component as ComponentInteractiveResponseCategorise)}
          banner={banner}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
        <DiscussionView
          {...(component as ComponentDiscussion)}
          onComponentExpand={expandMode =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              expandMode,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE && (
        <FillInBlanksSample
          {...(component as ComponentFillInBlanks)}
          banner={banner}
        />
      )}
    </>
  );
};

export default ActivitySampleComponent;
